import { c as _c } from "react/compiler-runtime";
import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Formik, useFormikContext } from 'formik';
import { useMutation } from '@apollo/client';
import avo from 'analytics/avo';
import { getUserFromCookie } from 'commons/localStorage';
import isClient from 'commons/isClient';
import Radio from 'components/essentials/Radio';
import CtaButton from 'components/essentials/Cta/CtaButton';
import Checkbox from 'components/consumer/Checkbox';
import { SUBMIT_BUSINESS_APPLICATION } from '@/graphql/mutations';
import LabeledTextarea from 'components/consumer/LabeledTextarea';
import LabeledTextInput from 'components/consumer/LabeledTextInput';
import ZipField from 'components/consumer/checkout/desktop/components/Shipping/components/ZipField';
import CityField from 'components/consumer/checkout/desktop/components/Shipping/components/CityField';
import StateField from 'components/consumer/checkout/desktop/components/Shipping/components/StateField';
import EmailField from 'components/consumer/checkout/desktop/components/EmailForm/components/EmailField';
import LastNameField from 'components/consumer/checkout/desktop/components/Shipping/components/LastNameField';
import ApartmentField from 'components/consumer/checkout/desktop/components/Shipping/components/ApartmentField';
import FirstNameField from 'components/consumer/checkout/desktop/components/Shipping/components/FirstNameField';
import PhoneNumberField from 'components/consumer/checkout/desktop/components/Shipping/components/PhoneNumberField';
import NewsletterOptIn from 'components/consumer/NewsletterOptIn';
import TwoColumnFormFields from 'components/consumer/TwoColumnFormFields';
import ValidationError from 'components/consumer/ValidationError';
import urlAffiliateFields from 'commons/urlAffiliateFields';
import TradeFormFileUpload from './components/TradeFormFileUpload';
import ConsumerBusinessTradeFormSuccess from './components/ConsumerBusinessTradeFormSuccess';
import useFBTradeSignup from './hooks/useFBTradeSignup';
import { INITIAL_VALUES, formSchema, PROFESSIONS, MEMBERSHIPS, INTERESTS } from './constants';
const TradeFormFileUploadFormik = () => {
  const $ = _c(5);
  const {
    setFieldValue,
    touched,
    errors
  } = useFormikContext();
  let t0;
  if ($[0] !== setFieldValue) {
    t0 = urls => setFieldValue("attachment_urls", urls);
    $[0] = setFieldValue;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleUrlsChange = t0;
  const t1 = touched.attachment_urls && errors.attachment_urls;
  let t2;
  if ($[2] !== handleUrlsChange || $[3] !== t1) {
    t2 = <TradeFormFileUpload onUrlsChange={handleUrlsChange} errorMessage={t1} />;
    $[2] = handleUrlsChange;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const ConsumerBusinessTradeForm = ({
  isAdmin,
  config
}) => {
  const [saved, setSaved] = useState(false);
  const containerRef = useRef(null);
  const [submitBusinessApplication] = useMutation(SUBMIT_BUSINESS_APPLICATION);
  useFBTradeSignup();
  useEffect(() => {
    if (isAdmin) {
      return;
    }
    if (isClient() && document.location.hash.includes('#consumer-business-trade-form')) {
      setTimeout(() => {
        containerRef.current?.previousSibling?.scrollIntoView();
      }, 2000);
    }
    if (isClient()) {
      const user = getUserFromCookie();
      avo.tradeApplicationVisited({
        ...urlAffiliateFields(),
        userId_: user?.id?.toString() ?? '',
        email: user?.email ?? '',
        backendEmail: user?.email ?? ''
      });
    }
  }, [isAdmin]);
  if (isAdmin) {
    return <div>
        <h1>Consumer Business Trade Form</h1>
      </div>;
  }
  const handleSubmit = async (values, actions) => {
    avo.ctaClicked({
      cta: 'Submit Trade Application',
      targetUrl: '',
      text: 'Apply',
      component: avo.Component.TRADE_APPLICATION,
      location: window.location.pathname.includes('/business') ? avo.Location.BUSINESS : avo.Location.TRADE_APPLICATION
    });
    const formValues = {
      designer_first_name: values.designer_first_name,
      designer_last_name: values.designer_last_name,
      designer_email: values.designer_email,
      designer_phone: values.designer_phone,
      designer_mailing: values.designer_mailing,
      designer_mailing_2: values.designer_mailing_2,
      designer_city: values.designer_city,
      designer_state: values.designer_state,
      designer_zip: values.designer_zip,
      designer_profession: values.designer_profession,
      profession_other: values.profession_other,
      designer_company: values.designer_company,
      designer_website: values.designer_website,
      designer_title: values.designer_title,
      designer_organizations: values.designer_organizations,
      designer_organizations_other: values.designer_organizations_other,
      designer_interests: values.designer_interests,
      designer_interests_other: values.designer_interests_other,
      store_sq_footage: values.store_sq_footage,
      yearly_upholst_sale: values.yearly_upholst_sale,
      tax_exempt: values.tax_exempt,
      attachment_urls: values.attachment_urls,
      refer_origin: window.document.referrer,
      request_page: window.location.pathname.replace(/^\/|\/$/g, '')
    };
    INTERESTS.forEach(interest => {
      if (values[interest]) {
        if (!formValues.designer_interests) {
          formValues.designer_interests = [];
        }
        formValues.designer_interests.push(interest);
      }
    });
    MEMBERSHIPS.forEach(membership => {
      if (values[membership]) {
        if (!formValues.designer_organizations) {
          formValues.designer_organizations = [];
        }
        formValues.designer_organizations.push(membership);
      }
    });
    try {
      actions.setFieldError('formErrors', '');
      const res = await submitBusinessApplication({
        variables: {
          form: formValues
        }
      });
      if (res.data.businessRequest.success) {
        const user_0 = getUserFromCookie();
        avo.tradeApplicationSubmitted({
          ...urlAffiliateFields(),
          userId_: user_0?.id?.toString() ?? '',
          email: values.designer_email,
          backendEmail: values.designer_email,
          optedInTrade: values.newsletter_opt_in
        });
        avo.emailAcquired({
          userId_: '',
          email: values.designer_email,
          optedInMarketing: values.newsletter_opt_in,
          location: window.location.pathname.includes('/business') ? avo.Location.BUSINESS : avo.Location.TRADE_APPLICATION,
          backendEmail: values.designer_email,
          component: avo.Component.TRADE_APPLICATION,
          talkativeCustomerId: ''
        });
        setSaved(true);
        containerRef.current.scrollIntoView();
      } else {
        actions.setFieldError('formErrors', res.data.businessRequest.message || 'Unable to submit application');
      }
    } catch (e) {
      actions.setFieldError('formErrors', 'Unable to submit application');
    } finally {
      actions.setSubmitting(false);
    }
  };
  return <div ref={containerRef} id={config?.elementAttribute?.id ?? 'consumer-business-trade-form'} data-xc="consumer-business-trade-form" className="max-w-[35rem] mx-auto my-8 max-[575px]:max-w-[85%]">
      {saved ? <ConsumerBusinessTradeFormSuccess /> : <>
          <h2 className="text-3xl font-bold">
            Apply Now for Trade and Business Approval
          </h2>
          <div className="text-base">
            If you&apos;re an architect, designer, developer, or retailer, enjoy
            a trade program that gives you unparalleled access to source Joybird
            pieces at great prices.
          </div>
          <Formik initialValues={INITIAL_VALUES} validationSchema={formSchema} onSubmit={handleSubmit} render={({
        values: values_0,
        errors,
        touched,
        handleSubmit: formikSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
        setFieldTouched,
        isSubmitting
      }) => <form onSubmit={formikSubmit} className="mt-10 mx-0 mb-16">
                <p className="text-sm text-gray-light1 m-0">
                  * denotes mandatory fields
                </p>

                <h3 className="text-lg font-bold pb-1 mt-9 mx-0 mb-[.825rem] [border-bottom:1px_solid_theme(colors.gray.light6)]">
                  I work in/as: *
                </h3>

                {PROFESSIONS.map(item => <Radio key={item} name="designer_profession" onChange={() => setFieldValue('designer_profession', item)} checked={values_0.designer_profession === item}>
                    <span className="text-sm font-bold">{item}</span>
                  </Radio>)}
                {touched.designer_profession && errors.designer_profession && <ValidationError block>
                    {errors.designer_profession}
                  </ValidationError>}
                {values_0.designer_profession === 'Other' && <div className="mb-4">
                    <LabeledTextInput id="profession_other" name="profession_other" data-xc="profession_other" handleBlur={handleBlur} onChange={handleChange} label="Explain your profession" value={values_0.profession_other} errorMessage={touched.profession_other && errors.profession_other} />
                  </div>}
                <p className="text-sm text-gray-light1 m-0">
                  **Joybird products are intended for residential use only.
                  Warranty excludes use in hospitality and commercial settings.
                </p>

                <div className="mb-16">
                  <h3 className="text-lg font-bold pb-1 mt-9 mx-0 mb-[.825rem] [border-bottom:1px_solid_theme(colors.gray.light6)]">
                    Personal information:
                  </h3>

                  <EmailField label="Email *" id="designer_email" name="designer_email" autoComplete="email" data-xc="designer_email" handleBlur={handleBlur} handleChange={handleChange} value={values_0.designer_email} error={errors.designer_email} isTouched={touched.designer_email} />

                  <TwoColumnFormFields>
                    <FirstNameField id="designer_first_name" name="designer_first_name" data-xc="designer_first_name" handleBlur={handleBlur} handleChange={handleChange} error={errors.designer_first_name} value={values_0.designer_first_name} isTouched={touched.designer_first_name} />
                    <LastNameField id="designer_last_name" name="designer_last_name" data-xc="designer_last_name" handleBlur={handleBlur} handleChange={handleChange} error={errors.designer_last_name} value={values_0.designer_last_name} isTouched={touched.designer_last_name} />
                  </TwoColumnFormFields>

                  <LabeledTextInput label="Title" id="designer_title" autoComplete="honorific-prefix" name="designer_title" data-xc="designer_title" handleBlur={handleBlur} onChange={handleChange} error={errors.designer_title} value={values_0.designer_title} isTouched={touched.designer_title} />

                  <TwoColumnFormFields>
                    <LabeledTextInput label="Company *" id="designer_company" autoComplete="organization" name="designer_company" data-xc="designer_company" handleBlur={handleBlur} onChange={handleChange} value={values_0.designer_company} errorMessage={touched.designer_company && errors.designer_company} />
                    <LabeledTextInput label="Website *" id="designer_website" autoComplete="url" name="designer_website" data-xc="designer_website" handleBlur={handleBlur} onChange={handleChange} value={values_0.designer_website} errorMessage={touched.designer_website && errors.designer_website} />
                  </TwoColumnFormFields>

                  <LabeledTextInput label="Address *" id="designer_mailing" name="designer_mailing" autoComplete="address-line1" data-xc="designer_mailing" handleBlur={handleBlur} onChange={handleChange} value={values_0.designer_mailing} errorMessage={touched.designer_mailing && errors.designer_mailing} />

                  <TwoColumnFormFields>
                    <ApartmentField showError id="designer_mailing_2" name="designer_mailing_2" data-xc="designer_mailing_2" handleBlur={handleBlur} handleChange={handleChange} placeholder="Apartment / Suite #" value={values_0.designer_mailing_2} error={errors.designer_mailing_2} isTouched={touched.designer_mailing_2} />
                    <CityField id="designer_city" name="designer_city" data-xc="designer_city" value={values_0.designer_city} error={errors.designer_city} isTouched={touched.designer_city} handleBlur={handleBlur} handleChange={handleChange} />
                  </TwoColumnFormFields>

                  <TwoColumnFormFields>
                    <StateField id="designer_state" name="designer_state" data-xc="designer_state" handleBlur={handleBlur} setFieldValue={setFieldValue} value={values_0.designer_state} error={errors.designer_state} isTouched={touched.designer_state} setFieldTouched={setFieldTouched} />
                    <ZipField id="designer_zip" name="designer_zip" data-xc="designer_zip" handleBlur={handleBlur} handleChange={handleChange} value={values_0.designer_zip} error={errors.designer_zip} isTouched={touched.designer_zip} />
                  </TwoColumnFormFields>

                  <PhoneNumberField id="designer_phone" name="designer_phone" data-xc="designer_phone" placeholder="Phone *" handleBlur={handleBlur} handleChange={handleChange} error={errors.designer_phone} value={values_0.designer_phone} isTouched={touched.designer_phone} />
                </div>

                <h3 className="text-lg font-bold pb-1 mt-9 mx-0 mb-[.825rem] [border-bottom:1px_solid_theme(colors.gray.light6)]">
                  Tell us about your Business/Project:
                </h3>

                <div className="mb-4">
                  <LabeledTextarea label="Message *" name="designer_interests_other" data-xc="designer_interests_other" onBlur={handleBlur} onChange={handleChange} value={values_0.designer_interests_other} errorMessage={touched.designer_interests_other && errors.designer_interests_other} maxLength={10000} autoComplete="off" rows={4} />
                </div>

                <TradeFormFileUploadFormik />

                <Checkbox name="tax_exempt" checked={values_0.tax_exempt} onClick={() => setFieldValue('tax_exempt', !values_0.tax_exempt)} size="xs" className="mx-0 my-[1.2em]">
                  I would like my account set up as tax-exempt
                </Checkbox>

                <div>
                  <NewsletterOptIn onClick={isChecked => setFieldValue('newsletter_opt_in', isChecked)} checked={values_0.newsletter_opt_in} />
                </div>

                {errors.formErrors && <ValidationError className="pb-2 text-center" block>
                    {errors.formErrors}
                  </ValidationError>}

                <CtaButton type="submit" color="black" styling="solid-button" data-xc="business-form-button" disabled={isSubmitting} block isLoading={isSubmitting}>
                  Apply
                </CtaButton>
              </form>} />
        </>}
    </div>;
};
ConsumerBusinessTradeForm.propTypes = {
  isAdmin: PropTypes.bool,
  config: PropTypes.shape({
    elementAttribute: PropTypes.shape({
      id: PropTypes.string
    })
  })
};
export default ConsumerBusinessTradeForm;